import { useAuth0 } from "@auth0/auth0-react";
import React, { useEffect } from "react";
import { AiOutlineCheck, AiOutlineClose, AiOutlineUndo, AiOutlineCloseCircle, AiOutlineSave, AiOutlineSearch, AiOutlineFilter, AiOutlineCopy, AiOutlineEye } from "react-icons/ai";
import TransactionDetailDrawer from "./TransactionDetailDrawer";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  useTransactionsQuery,
  useEditTransactionMutation,
  useDeleteTransactionMutation,
  useRestoreTransactionMutation,
  useCreateTransactionMutation,
  useTransactionValuesForSelectQuery,
  TransactionFieldsFragment,
} from "../generated/graphql";
import ErrorMessage from "./ErrorMessage";
import Toast from "./Toast";
import { useToast } from "../hooks/useToast";
import { extractPostIdFromComposite, extractGroupIdFromComposite, parsePostId } from "../utils/idHelpers";
import { formatAccountLabel, getAvailableAccounts } from "../utils/accountSelection";
import { splitAmount, formatAmountInput, parseAmountToCents } from "../utils/transactionAmount";
import { formatAmount } from "../utils/formatAmount";

type EditValues = {
  date: string;
  comment: string;
  groupId: string;
  userId: string | null;
  contextId: string;
  postId: string;
  accountId: string;
  amount: string;
};

const Transactions: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const [commentFilterInput, setCommentFilterInput] = React.useState<string>("");
  const [commentFilter, setCommentFilter] = React.useState<string>("");
  const [groupFilterInput, setGroupFilterInput] = React.useState<string>("");
  const [groupFilter, setGroupFilter] = React.useState<string>("");
  const [userFilterInput, setUserFilterInput] = React.useState<string>("");
  const [userFilter, setUserFilter] = React.useState<string>("");
  const [accountFilterInput, setAccountFilterInput] = React.useState<string>("");
  const [accountFilter, setAccountFilter] = React.useState<string>("");
  const [contextFilterInput, setContextFilterInput] = React.useState<string>("");
  const [contextFilter, setContextFilter] = React.useState<string>("");
  const [postFilterInput, setPostFilterInput] = React.useState<string>("");
  const [postFilter, setPostFilter] = React.useState<string>("");
  const [autoMatchLabelFilter, setAutoMatchLabelFilter] = React.useState<string>("");
  const [startDate, setStartDate] = React.useState<Date | null>(null);
  const [endDate, setEndDate] = React.useState<Date | null>(null);
  const [dateFilterStart, setDateFilterStart] = React.useState<Date | null>(null);
  const [dateFilterEnd, setDateFilterEnd] = React.useState<Date | null>(null);
  const [amountMinInput, setAmountMinInput] = React.useState<string>("");
  const [amountMaxInput, setAmountMaxInput] = React.useState<string>("");
  const [amountMinFilter, setAmountMinFilter] = React.useState<string>("");
  const [amountMaxFilter, setAmountMaxFilter] = React.useState<string>("");
  const [isInitialMount, setIsInitialMount] = React.useState<boolean>(true);
  
  const { loading, data, error, refetch } = useTransactionsQuery({
    skip: !isAuthenticated,
    variables: {
      comment: commentFilter || undefined,
      groupIds: groupFilter ? [extractGroupIdFromComposite(groupFilter)] : undefined,
      userIds: userFilter ? [userFilter] : undefined,
      accountIds: accountFilter ? [accountFilter] : undefined,
      contextIds: contextFilter ? [contextFilter] : undefined,
      postIds: postFilter ? [extractPostIdFromComposite(postFilter)] : undefined,
      after: dateFilterStart ? dateFilterStart.toISOString().split('T')[0] : undefined,
      before: dateFilterEnd ? dateFilterEnd.toISOString().split('T')[0] : undefined,
      autoMatchLabel: autoMatchLabelFilter || undefined,
      amountMin: parseAmountToCents(amountMinFilter),
      amountMax: parseAmountToCents(amountMaxFilter),
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  });

  const handleSearch = async () => {
    setCommentFilter(commentFilterInput);
    setGroupFilter(groupFilterInput);
    setUserFilter(userFilterInput);
    setAccountFilter(accountFilterInput);
    setContextFilter(contextFilterInput);
    setPostFilter(postFilterInput);
    setDateFilterStart(startDate);
    setDateFilterEnd(endDate);
    setAmountMinFilter(amountMinInput);
    setAmountMaxFilter(amountMaxInput);
    // Forcer le refetch immédiatement avec les nouvelles valeurs
    await refetch({
      comment: commentFilterInput || undefined,
      groupIds: groupFilterInput ? [extractGroupIdFromComposite(groupFilterInput)] : undefined,
      userIds: userFilterInput ? [userFilterInput] : undefined,
      accountIds: accountFilterInput ? [accountFilterInput] : undefined,
      contextIds: contextFilterInput ? [contextFilterInput] : undefined,
      postIds: postFilterInput ? [extractPostIdFromComposite(postFilterInput)] : undefined,
      after: startDate ? startDate.toISOString().split('T')[0] : undefined,
      before: endDate ? endDate.toISOString().split('T')[0] : undefined,
      amountMin: parseAmountToCents(amountMinInput),
      amountMax: parseAmountToCents(amountMaxInput),
    });
  };

  const handleClearFilter = async () => {
    setCommentFilterInput("");
    setCommentFilter("");
    setGroupFilterInput("");
    setGroupFilter("");
    setUserFilterInput("");
    setUserFilter("");
    setAccountFilterInput("");
    setAccountFilter("");
    setContextFilterInput("");
    setContextFilter("");
    setPostFilterInput("");
    setPostFilter("");
    setAutoMatchLabelFilter("");
    setStartDate(null);
    setEndDate(null);
    setDateFilterStart(null);
    setDateFilterEnd(null);
    setAmountMinInput("");
    setAmountMaxInput("");
    setAmountMinFilter("");
    setAmountMaxFilter("");
    // Forcer le refetch sans filtres
    await refetch({
      comment: undefined,
      groupIds: undefined,
      userIds: undefined,
      accountIds: undefined,
      contextIds: undefined,
      postIds: undefined,
      after: undefined,
      before: undefined,
      autoMatchLabel: undefined,
      amountMin: undefined,
      amountMax: undefined,
    });
  };

  // Refetch automatiquement quand les filtres changent (sauf au montage initial)
  useEffect(() => {
    if (isInitialMount) {
      setIsInitialMount(false);
      return;
    }
    if (isAuthenticated) {
      refetch({
        comment: commentFilter || undefined,
        groupIds: groupFilter ? [extractGroupIdFromComposite(groupFilter)] : undefined,
        userIds: userFilter ? [userFilter] : undefined,
        accountIds: accountFilter ? [accountFilter] : undefined,
        contextIds: contextFilter ? [contextFilter] : undefined,
        postIds: postFilter ? [extractPostIdFromComposite(postFilter)] : undefined,
        after: dateFilterStart ? dateFilterStart.toISOString().split('T')[0] : undefined,
        before: dateFilterEnd ? dateFilterEnd.toISOString().split('T')[0] : undefined,
        amountMin: parseAmountToCents(amountMinFilter),
        amountMax: parseAmountToCents(amountMaxFilter),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commentFilter, groupFilter, userFilter, accountFilter, contextFilter, postFilter, dateFilterStart, dateFilterEnd, amountMinFilter, amountMaxFilter, isAuthenticated]);
  const { data: transactionValuesData } = useTransactionValuesForSelectQuery({
    skip: !isAuthenticated,
  });
  const [editTransaction, { loading: editing }] = useEditTransactionMutation();
  const [deleteTransaction, { loading: deleting }] = useDeleteTransactionMutation();
  const [restoreTransaction, { loading: restoring }] = useRestoreTransactionMutation();
  const [duplicateTransaction, { loading: duplicating }] = useCreateTransactionMutation();
  const [createTransactionFromForm, { loading: creating }] = useCreateTransactionMutation();
  const [editingTransactionIds, setEditingTransactionIds] = React.useState<Set<string>>(new Set());
  const [activeEdit, setActiveEdit] = React.useState<{ id: string; values: EditValues } | null>(null);
  const [deletedTransactionIds, setDeletedTransactionIds] = React.useState<Set<string>>(new Set());
  const [newTransactions, setNewTransactions] = React.useState<any[]>([]);
  const { toast, setToast } = useToast();
  const [showFilters, setShowFilters] = React.useState<boolean>(false);
  const [drawerTransaction, setDrawerTransaction] = React.useState<TransactionFieldsFragment | null>(null);
  const [newTransactionForm, setNewTransactionForm] = React.useState<{
    date: string;
    comment: string;
    groupId: string;
    userId: string | null;
    contextId: string;
    postId: string;
    accountId: string;
    amount: string;
  }>({
    date: new Date().toISOString().split('T')[0],
    comment: '',
    groupId: '',
    userId: null,
    contextId: '',
    postId: '',
    accountId: '',
    amount: '',
  });

  const resetNewTransactionForm = () => {
    setNewTransactionForm({
      date: new Date().toISOString().split('T')[0],
      comment: '',
      groupId: '',
      userId: null,
      contextId: '',
      postId: '',
      accountId: '',
      amount: '',
    });
  };


  const formatDate = (date: any): string => {
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  // Obtenir les users disponibles pour un groupe donné
  const getUsersForGroup = (groupId: string) => {
    if (!transactionValuesData?.groups) return [];
    const group = transactionValuesData.groups.find((g) => g.id === groupId);
    return group?.users || [];
  };

  // Obtenir tous les utilisateurs de tous les groupes (pour le filtre indépendant)
  const getAllUsers = () => {
    if (!transactionValuesData?.groups) return [];
    const allUsersMap = new Map<string, { id: string; firstname: string }>();
    transactionValuesData.groups.forEach((group) => {
      group.users.forEach((user) => {
        if (!allUsersMap.has(user.id)) {
          allUsersMap.set(user.id, user);
        }
      });
    });
    return Array.from(allUsersMap.values());
  };

  // Obtenir tous les comptes avec leur utilisateur (format: "<user> - <compte>")
  const getAllAccountsWithUser = () => {
    if (!transactionValuesData?.groups) return [];
    const accountsMap = new Map<
      string,
      { id: string; label: string; enabled?: boolean | null; userId?: string }
    >();

    transactionValuesData.groups.forEach((group) => {
      group.users.forEach((user) => {
        user.accounts.forEach((account) => {
          if (!accountsMap.has(account.id)) {
            accountsMap.set(account.id, {
              id: account.id,
              label: `${user.firstname} - ${account.name}`,
              enabled: account.enabled,
              userId: user.id,
            });
          }
        });
      });

      group.accounts.forEach((account) => {
        if (!accountsMap.has(account.id)) {
          accountsMap.set(account.id, {
            id: account.id,
            label: account.name,
            enabled: account.enabled,
          });
        }
      });
    });

    return Array.from(accountsMap.values());
  };

  // Obtenir tous les contexts de tous les groupes (dédupliqués par id)
  const getAllContexts = () => {
    if (!transactionValuesData?.groups) return [];
    const seen = new Map<string, { id: string; name: string }>();
    transactionValuesData.groups.forEach((group) => {
      group.contexts.forEach((context) => {
        if (!seen.has(context.id)) {
          seen.set(context.id, { id: context.id, name: context.name });
        }
      });
    });
    return Array.from(seen.values());
  };

  // Obtenir tous les posts de tous les groupes
  const getAllPosts = () => {
    if (!transactionValuesData?.groups) return [];
    const seen = new Map<string, { id: string; label: string }>();

    transactionValuesData.groups.forEach((group) => {
      group.contexts.forEach((context) => {
        context.posts.forEach((post) => {
          const rawPostId = parsePostId(post.id).postId;
          if (!seen.has(rawPostId)) {
            seen.set(rawPostId, {
              id: post.id,
              label: `${context.name} - ${post.name}`,
            });
          }
        });
      });
    });

    return Array.from(seen.values());
  };


  // Obtenir les contexts disponibles pour un groupe donné
  const getContextsForGroup = (groupId: string) => {
    if (!transactionValuesData?.groups) return [];
    const group = transactionValuesData.groups.find((g) => g.id === groupId);
    return group?.contexts || [];
  };

  // Obtenir les posts disponibles pour un context donné
  const getPostsForContext = (groupId: string, contextId: string) => {
    if (!transactionValuesData?.groups || !contextId) return [];
    const group = transactionValuesData.groups.find((g) => g.id === groupId);
    if (!group) return [];
    const context = group.contexts.find((c) => c.id === contextId);
    return context?.posts || [];
  };

  // Fonction helper pour trouver le post correspondant même si le groupId diffère dans l'ID composite
  const findMatchingPostId = (targetPostId: string, targetGroupId: string, targetContextId: string): string | null => {
    if (!transactionValuesData?.groups || !targetContextId) return null;
    
    const group = transactionValuesData.groups.find((g) => g.id === targetGroupId);
    if (!group) return null;
    
    const context = group.contexts.find((c) => c.id === targetContextId);
    if (!context) return null;
    
    // Chercher d'abord une correspondance exacte
    const exactMatch = context.posts.find((p) => p.id === targetPostId);
    if (exactMatch) {
      return exactMatch.id;
    }
    
    // Si pas de correspondance exacte, essayer de trouver par contextId et postId réel
    try {
      const targetParsed = parsePostId(targetPostId);
      const matchingPost = context.posts.find((p) => {
        try {
          const parsed = parsePostId(p.id);
          return parsed.contextId === targetParsed.contextId && parsed.postId === targetParsed.postId;
        } catch {
          return false;
        }
      });
      if (matchingPost) {
        return matchingPost.id;
      }
    } catch {
      // Si l'ID n'est pas composite, retourner null
    }
    
    return null;
  };

  // Vérifier si un context existe dans un groupe
  const contextExistsInGroup = (groupId: string, contextId: string): boolean => {
    if (!contextId) return false;
    const contexts = getContextsForGroup(groupId);
    return contexts.some((c) => c.id === contextId);
  };

  // Vérifier si un post existe dans un context
  const postExistsInContext = (groupId: string, contextId: string, postId: string): boolean => {
    if (!postId || !contextId) return false;
    const posts = getPostsForContext(groupId, contextId);
    return posts.some((p) => p.id === postId);
  };

  // Vérifier si un user existe dans un groupe
  const userExistsInGroup = (groupId: string, userId: string | null): boolean => {
    if (!userId) return false;
    const users = getUsersForGroup(groupId);
    return users.some((u) => u.id === userId);
  };

  const handleTransactionClick = (transaction: TransactionFieldsFragment, event: React.MouseEvent) => {
    if ((event.target as HTMLElement).closest('button') || (event.target as HTMLElement).closest('input') || (event.target as HTMLElement).closest('textarea') || (event.target as HTMLElement).closest('select')) {
      return;
    }
    setDrawerTransaction(null);
    const dateObj = new Date(transaction.date);
    const matchingPostId = findMatchingPostId(transaction.post.id, transaction.group.id, transaction.context.id);
    setActiveEdit({
      id: transaction.id,
      values: {
        date: dateObj.toISOString().split('T')[0],
        comment: transaction.comment || '',
        groupId: transaction.group.id,
        userId: transaction.user?.id || null,
        contextId: transaction.context.id,
        postId: matchingPostId || transaction.post.id,
        accountId: transaction.account.id,
        amount: formatAmountInput(transaction.credit, transaction.debit),
      },
    });
  };

  const handleCancelEdit = (event: React.MouseEvent) => {
    event.stopPropagation();
    setActiveEdit(null);
  };

  const handleEditValueChange = (
    field: 'date' | 'comment' | 'groupId' | 'userId' | 'contextId' | 'postId' | 'accountId' | 'amount',
    value: string | null
  ) => {
    setActiveEdit((prev) => {
      if (!prev) return null;
      const currentValues = prev.values;
      const updated = { ...currentValues, [field]: value } as EditValues;

      if (field === 'groupId' && value) {
        const newGroupId = value;
        if (currentValues.userId && !userExistsInGroup(newGroupId, currentValues.userId)) {
          updated.userId = null;
        }
        const accounts = getAvailableAccounts(transactionValuesData, newGroupId, updated.userId || currentValues.userId, currentValues.accountId);
        if (currentValues.accountId && !accounts.some((a) => a.id === currentValues.accountId)) {
          updated.accountId = accounts.length > 0 ? accounts[0].id : '';
        }
        if (currentValues.contextId && !contextExistsInGroup(newGroupId, currentValues.contextId)) {
          updated.contextId = '';
          updated.postId = '';
        } else if (currentValues.contextId && currentValues.postId) {
          if (!postExistsInContext(newGroupId, currentValues.contextId, currentValues.postId)) {
            updated.postId = '';
          }
        }
      }

      if (field === 'contextId' && value) {
        const currentGroupId = updated.groupId || currentValues.groupId;
        if (currentValues.postId && !postExistsInContext(currentGroupId, value, currentValues.postId)) {
          updated.postId = '';
        }
      }

      return { ...prev, values: updated };
    });
  };

  const handleCreateNewTransaction = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!newTransactionForm.groupId || !newTransactionForm.contextId || !newTransactionForm.postId || !newTransactionForm.accountId) {
      setToast({ message: "Veuillez remplir tous les champs obligatoires", type: "error" });
      return;
    }

    const normalizedAmount = newTransactionForm.amount.replace(',', '.').trim();
    const parsedAmount = Number(normalizedAmount);

    if (!normalizedAmount || !Number.isFinite(parsedAmount) || parsedAmount === 0) {
      setToast({ message: "Veuillez renseigner un montant valide non nul", type: "error" });
      return;
    }

    const { credit, debit } = splitAmount(newTransactionForm.amount);
    const selectedGroup = transactionValuesData?.groups.find((g) => g.id === newTransactionForm.groupId);
    const selectedContext = selectedGroup?.contexts.find((c) => c.id === newTransactionForm.contextId);
    const selectedPost = selectedContext?.posts.find((p) => p.id === newTransactionForm.postId);
    const selectedAccount = selectedGroup?.accounts.find((a) => a.id === newTransactionForm.accountId);
    const selectedUser = newTransactionForm.userId ? selectedGroup?.users.find((u) => u.id === newTransactionForm.userId) : null;

    if (!selectedGroup || !selectedContext || !selectedPost || !selectedAccount) {
      setToast({ message: "Impossible de déterminer les références de la transaction", type: "error" });
      return;
    }

    const optimisticId = `optimistic-${Date.now()}-${Math.random()}`;

    const optimisticTransaction = {
      __typename: 'Transaction' as const,
      id: optimisticId,
      date: newTransactionForm.date,
      credit,
      debit,
      checked: false,
      deleted: false,
      comment: newTransactionForm.comment || '',
      group: selectedGroup,
      user: selectedUser || null,
      post: selectedPost,
      context: selectedContext,
      account: selectedAccount,
    };

    setNewTransactions((prev) => [optimisticTransaction, ...prev]);
    const formData = { ...newTransactionForm };
    resetNewTransactionForm();

    try {
      const realGroupId = extractGroupIdFromComposite(formData.groupId);
      const realPostId = extractPostIdFromComposite(formData.postId);

      const result = await createTransactionFromForm({
        variables: {
          date: formData.date,
          credit,
          debit,
          groupId: realGroupId,
          userId: formData.userId || null,
          postId: realPostId,
          contextId: formData.contextId,
          accountId: formData.accountId,
          comment: formData.comment || '',
          checked: false,
        },
      });

      if (result.data?.createTransaction) {
        setNewTransactions((prev) =>
          prev.map((transaction) => (transaction.id === optimisticId ? result.data!.createTransaction : transaction))
        );
      }

      setToast({ message: "Transaction créée", type: "success" });
    } catch (err: any) {
      console.error("Erreur lors de la création de la transaction:", err);
      setNewTransactions((prev) => prev.filter((transaction) => transaction.id !== optimisticId));
      const errorMessage = err?.message || "Une erreur est survenue lors de la création";
      setToast({ message: errorMessage, type: "error" });
    }
  };

  const handleSaveEdit = async (transaction: {
    id: string;
    date: any;
    credit: number;
    debit: number;
    group: { id: string };
    user?: { id: string } | null;
    post: { id: string };
    context: { id: string };
    account: { id: string };
    comment: string;
    checked: boolean;
  }, event: React.MouseEvent) => {
    event.stopPropagation();
    
    if (!activeEdit || activeEdit.id !== transaction.id) return;

    const values = activeEdit.values;
    const amountInput = values.amount;
    const normalizedAmount = amountInput.replace(',', '.').trim();
    const parsedAmount = Number(normalizedAmount);

    if (!normalizedAmount || !Number.isFinite(parsedAmount)) {
      setToast({ message: "Veuillez renseigner un montant valide", type: "error" });
      return;
    }

    const { credit, debit } = splitAmount(amountInput);

    setEditingTransactionIds((prev) => new Set(prev).add(transaction.id));

    try {
      const realGroupId = extractGroupIdFromComposite(values.groupId || transaction.group.id);
      const realPostId = extractPostIdFromComposite(values.postId || transaction.post.id);
      const editedDate = new Date(values.date);

      await editTransaction({
        variables: {
          id: transaction.id,
          date: editedDate.toISOString().split('T')[0],
          credit,
          debit,
          groupId: realGroupId,
          userId: values.userId || null,
          postId: realPostId,
          contextId: values.contextId || transaction.context.id,
          accountId: values.accountId || transaction.account.id,
          comment: values.comment,
          checked: transaction.checked,
        },
      });

      setActiveEdit(null);
    } catch (err: any) {
      console.error("Erreur lors de la modification de la transaction:", err);
      const errorMessage = err?.message || "Une erreur est survenue lors de la modification de la transaction";
      setToast({ message: errorMessage, type: "error" });
    } finally {
      // Retirer la transaction de l'ensemble après la mutation (même en cas d'erreur)
      setEditingTransactionIds((prev) => {
        const next = new Set(prev);
        next.delete(transaction.id);
        return next;
      });
    }
  };

  const handleDeleteTransaction = async (transactionId: string) => {
    // Ajouter la transaction à l'ensemble des transactions en cours d'édition (passe en bleu)
    setEditingTransactionIds((prev) => new Set(prev).add(transactionId));
    
    try {
      await deleteTransaction({
        variables: {
          id: transactionId,
        },
      });
      
      setDeletedTransactionIds((prev) => new Set(prev).add(transactionId));
      setActiveEdit(null);
      
      setToast({ message: "Transaction supprimée", type: "success" });
    } catch (err: any) {
      console.error("Erreur lors de la suppression de la transaction:", err);
      const errorMessage = err?.message || "Une erreur est survenue lors de la suppression";
      setToast({ message: errorMessage, type: "error" });
    } finally {
      // Retirer la transaction de l'ensemble après la mutation (même en cas d'erreur)
      setEditingTransactionIds((prev) => {
        const next = new Set(prev);
        next.delete(transactionId);
        return next;
      });
    }
  };

  const handleRestoreTransaction = async (transactionId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    
    // Ajouter la transaction à l'ensemble des transactions en cours d'édition (passe en bleu)
    setEditingTransactionIds((prev) => new Set(prev).add(transactionId));
    
    try {
      await restoreTransaction({
        variables: {
          id: transactionId,
        },
      });
      
      // Retirer la transaction de la liste des supprimées
      setDeletedTransactionIds((prev) => {
        const next = new Set(prev);
        next.delete(transactionId);
        return next;
      });
      
      setToast({ message: "Transaction restaurée", type: "success" });
    } catch (err: any) {
      console.error("Erreur lors de la restauration de la transaction:", err);
      const errorMessage = err?.message || "Une erreur est survenue lors de la restauration";
      setToast({ message: errorMessage, type: "error" });
    } finally {
      // Retirer la transaction de l'ensemble après la mutation (même en cas d'erreur)
      setEditingTransactionIds((prev) => {
        const next = new Set(prev);
        next.delete(transactionId);
        return next;
      });
    }
  };

  const handleCheckTransaction = async (
    transaction: {
      id: string;
      date: any;
      credit: number;
      debit: number;
      group: { id: string };
      user?: { id: string } | null;
      post: { id: string };
      context: { id: string };
      account: { id: string };
      comment: string;
    },
    event: React.MouseEvent
  ) => {
    // Empêcher la propagation du clic pour ne pas ouvrir le mode édition
    event.stopPropagation();
    
    // Ajouter la transaction à l'ensemble des transactions en cours d'édition
    setEditingTransactionIds((prev) => new Set(prev).add(transaction.id));
    
    try {
      // Extraire les IDs réels des IDs composites pour les mutations
      const realGroupId = extractGroupIdFromComposite(transaction.group.id);
      const realPostId = extractPostIdFromComposite(transaction.post.id);
      
      await editTransaction({
        variables: {
          id: transaction.id,
          date: transaction.date,
          credit: transaction.credit,
          debit: transaction.debit,
          groupId: realGroupId,
          userId: transaction.user?.id || null,
          postId: realPostId,
          contextId: transaction.context.id,
          accountId: transaction.account.id,
          comment: transaction.comment,
          checked: true,
        },
      });
    } catch (err: any) {
      console.error("Erreur lors de la vérification de la transaction:", err);
      const errorMessage = err?.message || "Une erreur est survenue lors de la vérification de la transaction";
      setToast({ message: errorMessage, type: "error" });
    } finally {
      // Retirer la transaction de l'ensemble après la mutation (même en cas d'erreur)
      setEditingTransactionIds((prev) => {
        const next = new Set(prev);
        next.delete(transaction.id);
        return next;
      });
    }
  };

  const handleUncheckTransaction = async (
    transaction: {
      id: string;
      date: any;
      credit: number;
      debit: number;
      group: { id: string };
      user?: { id: string } | null;
      post: { id: string };
      context: { id: string };
      account: { id: string };
      comment: string;
    },
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    
    // Ajouter la transaction à l'ensemble des transactions en cours d'édition
    setEditingTransactionIds((prev) => new Set(prev).add(transaction.id));
    
    try {
      // Extraire les IDs réels des IDs composites pour les mutations
      const realGroupId = extractGroupIdFromComposite(transaction.group.id);
      const realPostId = extractPostIdFromComposite(transaction.post.id);
      
      await editTransaction({
        variables: {
          id: transaction.id,
          date: transaction.date,
          credit: transaction.credit,
          debit: transaction.debit,
          groupId: realGroupId,
          userId: transaction.user?.id || null,
          postId: realPostId,
          contextId: transaction.context.id,
          accountId: transaction.account.id,
          comment: transaction.comment,
          checked: false,
        },
      });
      
      setActiveEdit(null);
    } catch (err: any) {
      console.error("Erreur lors de la dévérification de la transaction:", err);
      const errorMessage = err?.message || "Une erreur est survenue lors de la dévérification de la transaction";
      setToast({ message: errorMessage, type: "error" });
    } finally {
      // Retirer la transaction de l'ensemble après la mutation (même en cas d'erreur)
      setEditingTransactionIds((prev) => {
        const next = new Set(prev);
        next.delete(transaction.id);
        return next;
      });
    }
  };

  const handleDuplicateTransaction = async (
    transaction: {
      id: string;
      date: any;
      credit: number;
      debit: number;
      group: { id: string };
      user?: { id: string } | null;
      post: { id: string };
      context: { id: string };
      account: { id: string };
      comment: string;
      checked: boolean;
    },
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    
    // Créer une transaction optimiste avec un ID temporaire
    const optimisticId = `optimistic-${Date.now()}-${Math.random()}`;
    const optimisticTransaction = {
      __typename: 'Transaction' as const,
      id: optimisticId,
      date: transaction.date,
      credit: transaction.credit,
      debit: transaction.debit,
      checked: false,
      deleted: false,
      comment: transaction.comment,
      group: transaction.group,
      user: transaction.user,
      post: transaction.post,
      context: transaction.context,
      account: transaction.account,
    };
    
    // Ajouter immédiatement la transaction optimiste en haut de la liste
    setNewTransactions((prev) => [optimisticTransaction, ...prev]);
    
    try {
      // Extraire les IDs réels des IDs composites pour les mutations
      const realGroupId = extractGroupIdFromComposite(transaction.group.id);
      const realPostId = extractPostIdFromComposite(transaction.post.id);
      
      const result = await duplicateTransaction({
        variables: {
          date: transaction.date,
          credit: transaction.credit,
          debit: transaction.debit,
          groupId: realGroupId,
          userId: transaction.user?.id || null,
          postId: realPostId,
          contextId: transaction.context.id,
          accountId: transaction.account.id,
          comment: transaction.comment,
          checked: false, // La transaction dupliquée n'est pas vérifiée par défaut
        },
      });
      
      if (result.data?.createTransaction) {
        // Remplacer la transaction optimiste par la vraie transaction
        setNewTransactions((prev) => 
          prev.map((t) => t.id === optimisticId ? result.data!.createTransaction : t)
        );
      }
      
      setToast({ message: "Transaction dupliquée", type: "success" });
    } catch (err: any) {
      console.error("Erreur lors de la duplication de la transaction:", err);
      // Retirer la transaction optimiste en cas d'erreur
      setNewTransactions((prev) => prev.filter((t) => t.id !== optimisticId));
      const errorMessage = err?.message || "Une erreur est survenue lors de la duplication";
      setToast({ message: errorMessage, type: "error" });
    }
  };

  // Les IDs sont déjà transformés en composites par le Apollo Link
  // On utilise directement les données du cache


  if (authLoading || loading) {
    return (
      <div className="container mx-auto">
        <h1 className="text-2xl font-bold mb-6">Liste de transactions</h1>
        <div className="text-center text-gray-500">Chargement...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="container mx-auto">
        <h1 className="text-2xl font-bold mb-6">Liste de transactions</h1>
        <div className="text-center text-red-500">
          Veuillez vous connecter pour accéder aux transactions
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto">
        <h1 className="text-2xl font-bold mb-6">Liste de transactions</h1>
        <ErrorMessage
          error={error}
          onRetry={() => refetch()}
          title="Erreur lors du chargement des transactions"
          className="mb-6"
        />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container mx-auto">
        <h1 className="text-2xl font-bold mb-6">Liste de transactions</h1>
        <ErrorMessage
          onRetry={() => refetch()}
          title="Erreur lors du chargement des transactions - pas de données"
          className="mb-6"
        />
      </div>
    );
  }

  const uncheckedTransactions = data.uncheckedTransactions?.items || [];
  const checkedTransactions = data.listTransactions?.items || [];

  // Combiner et trier : d'abord les newTransactions (en haut), puis les unchecked, puis les checked
  // À l'intérieur de chaque groupe, trier par date (plus récentes en premier)
  // Créer des copies pour éviter de modifier les tableaux en lecture seule
  const sortedNewTransactions = Array.from(newTransactions);
  const sortedUnchecked = Array.from(uncheckedTransactions).sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );
  const sortedChecked = Array.from(checkedTransactions).sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );
  const allTransactions = [...sortedNewTransactions, ...sortedUnchecked, ...sortedChecked];

  return (
    <>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <div className="container mx-auto">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold">Liste de transactions</h1>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors flex items-center gap-2 text-sm font-medium"
              title={showFilters ? "Masquer les filtres" : "Afficher les filtres"}
            >
              <AiOutlineFilter className="text-lg" />
              {showFilters ? "Masquer les filtres" : "Afficher les filtres"}
            </button>
          </div>
          
          {showFilters && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Commentaire
                  </label>
                  <input
                    type="text"
                    placeholder="Rechercher dans les commentaires..."
                    value={commentFilterInput}
                    onChange={(e) => setCommentFilterInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Groupe
                  </label>
                  <select
                    value={groupFilterInput}
                    onChange={(e) => setGroupFilterInput(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Tous les groupes</option>
                    {transactionValuesData?.groups?.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Utilisateur
                  </label>
                  <select
                    value={userFilterInput}
                    onChange={(e) => setUserFilterInput(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Tous les utilisateurs</option>
                    {getAllUsers().map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.firstname}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Compte
                  </label>
                  <select
                    value={accountFilterInput}
                    onChange={(e) => setAccountFilterInput(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Tous les comptes</option>
                    {getAllAccountsWithUser().map((account) => (
                      <option key={account.id} value={account.id}>
                        {formatAccountLabel(account.label, account.enabled)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Context
                  </label>
                  <select
                    value={contextFilterInput}
                    onChange={(e) => setContextFilterInput(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Tous les contexts</option>
                    {getAllContexts().map((context) => (
                      <option key={context.id} value={context.id}>
                        {context.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Post
                  </label>
                  <select
                    value={postFilterInput}
                    onChange={(e) => setPostFilterInput(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Tous les posts</option>
                    {getAllPosts().map((post) => (
                      <option key={post.id} value={post.id}>
                        {post.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Période
                </label>
                <div className="flex items-center gap-2">
                  <DatePicker
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    selectsStart
                    startDate={startDate}
                    endDate={endDate}
                    maxDate={endDate || new Date()}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Date de début"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <span className="text-gray-500">→</span>
                  <DatePicker
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    selectsEnd
                    startDate={startDate}
                    endDate={endDate}
                    minDate={startDate || undefined}
                    maxDate={new Date()}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Date de fin"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Montant (€)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Montant min"
                    value={amountMinInput}
                    onChange={(e) => setAmountMinInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <span className="text-gray-500">→</span>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Montant max"
                    value={amountMaxInput}
                    onChange={(e) => setAmountMaxInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSearch}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors flex items-center gap-2 text-sm font-medium"
                  title="Rechercher"
                >
                  <AiOutlineSearch className="text-lg" />
                  Rechercher
                </button>
                {(commentFilter || groupFilter || userFilter || accountFilter || contextFilter || postFilter || dateFilterStart || dateFilterEnd || amountMinFilter || amountMaxFilter) && (
                  <button
                    onClick={handleClearFilter}
                    className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
                    title="Effacer les filtres"
                  >
                    <AiOutlineClose className="text-lg" />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4">
          {/* En-tête des colonnes */}
          <div className="grid grid-cols-12 gap-4 items-center mb-3 pb-2 border-b border-gray-300">
            <div className="col-span-1 text-xs font-semibold text-gray-700 uppercase">
              Date
            </div>
            <div className="col-span-1 text-xs font-semibold text-gray-700 uppercase">
              Group / User
            </div>
            <div className="col-span-2 text-xs font-semibold text-gray-700 uppercase">
              Compte
            </div>
            <div className="col-span-2 text-xs font-semibold text-gray-700 uppercase">
              Context / Post
            </div>
            <div className="col-span-3 text-xs font-semibold text-gray-700 uppercase">
              Commentaire
            </div>
            <div className="col-span-2 text-right text-xs font-semibold text-gray-700 uppercase">
              Montant
            </div>
            <div className="col-span-1 text-xs font-semibold text-gray-700 uppercase">
              Actions
            </div>
          </div>
          <div className="space-y-2">
            {/* Formulaire de création de transaction */}
            <form onSubmit={handleCreateNewTransaction}>
              <div className="rounded-md p-3 border bg-purple-50 border-purple-200">
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-1 text-sm font-medium text-gray-900">
                    <input
                      type="date"
                      value={newTransactionForm.date}
                      onChange={(e) => setNewTransactionForm({ ...newTransactionForm, date: e.target.value })}
                      required
                      className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="col-span-1 text-sm text-gray-700">
                    {transactionValuesData?.groups ? (
                      <>
                        <select
                          value={newTransactionForm.groupId}
                          onChange={(e) =>
                            setNewTransactionForm({
                              ...newTransactionForm,
                              groupId: e.target.value,
                              contextId: '',
                              postId: '',
                              accountId: '',
                              userId: null,
                            })
                          }
                          required
                          className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Groupe *</option>
                          {transactionValuesData.groups.map((group) => (
                            <option key={group.id} value={group.id}>
                              {group.name}
                            </option>
                          ))}
                        </select>
                        {newTransactionForm.groupId && (
                          <div className="mt-2">
                            <select
                              value={newTransactionForm.userId || ''}
                              onChange={(e) => setNewTransactionForm({ ...newTransactionForm, userId: e.target.value || null })}
                              className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="">Aucun utilisateur</option>
                              {getUsersForGroup(newTransactionForm.groupId).map((user) => (
                                <option key={user.id} value={user.id}>
                                  {user.firstname}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center text-gray-500">Chargement...</div>
                    )}
                  </div>
                  <div className="col-span-2 text-sm text-gray-700">
                    {newTransactionForm.groupId && transactionValuesData?.groups ? (
                      <select
                        value={newTransactionForm.accountId}
                        onChange={(e) => setNewTransactionForm({ ...newTransactionForm, accountId: e.target.value })}
                        required
                        className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Compte *</option>
                        {getAvailableAccounts(transactionValuesData, newTransactionForm.groupId, newTransactionForm.userId, undefined).map((account) => (
                          <option key={account.id} value={account.id}>
                            {formatAccountLabel(account.name, account.enabled)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="text-gray-400">-</div>
                    )}
                  </div>
                  <div className="col-span-2 text-sm text-gray-700">
                    {newTransactionForm.groupId && transactionValuesData?.groups ? (
                      <>
                        <select
                          value={newTransactionForm.contextId}
                          onChange={(e) =>
                            setNewTransactionForm({
                              ...newTransactionForm,
                              contextId: e.target.value,
                              postId: '',
                            })
                          }
                          required
                          className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-2"
                        >
                          <option value="">Context *</option>
                          {getContextsForGroup(newTransactionForm.groupId).map((context) => (
                            <option key={context.id} value={context.id}>
                              {context.name}
                            </option>
                          ))}
                        </select>
                        {newTransactionForm.contextId && (
                          <select
                            value={newTransactionForm.postId}
                            onChange={(e) => setNewTransactionForm({ ...newTransactionForm, postId: e.target.value })}
                            required
                            className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="">Post *</option>
                            {getPostsForContext(newTransactionForm.groupId, newTransactionForm.contextId).map((post) => (
                              <option key={post.id} value={post.id}>
                                {post.name}
                              </option>
                            ))}
                          </select>
                        )}
                      </>
                    ) : (
                      <div className="text-gray-400">-</div>
                    )}
                  </div>
                  <div className="col-span-3 text-sm text-gray-600 italic">
                    <textarea
                      value={newTransactionForm.comment}
                      onChange={(e) => setNewTransactionForm({ ...newTransactionForm, comment: e.target.value })}
                      rows={2}
                      className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      placeholder="Commentaire (optionnel)"
                    />
                  </div>
                  <div className="col-span-2 text-right text-sm font-semibold text-gray-900">
                    <div className="flex flex-col items-end">
                      <input
                        type="number"
                        value={newTransactionForm.amount}
                        onChange={(e) => setNewTransactionForm({ ...newTransactionForm, amount: e.target.value })}
                        step="0.01"
                        className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Montant (€)"
                      />
                    </div>
                  </div>
                  <div className="col-span-1 text-sm text-gray-500 flex items-center gap-1">
                    <button
                      type="submit"
                      disabled={creating}
                      className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Créer la transaction"
                    >
                      <AiOutlineSave className="text-lg" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        resetNewTransactionForm();
                      }}
                      className="p-1 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                      title="Réinitialiser le formulaire"
                    >
                      <AiOutlineClose className="text-lg" />
                    </button>
                  </div>
                </div>
              </div>
            </form>
            {allTransactions.map((transaction) => {
              const isOptimistic = transaction.id.startsWith('optimistic-');
              const isSaving = editingTransactionIds.has(transaction.id) || isOptimistic;
              const isInEditMode = activeEdit?.id === transaction.id;
              const editValues = isInEditMode ? activeEdit!.values : null;
              const isDeleted = deletedTransactionIds.has(transaction.id);
              return (
                <div
                  key={transaction.id}
                  onClick={(e) => handleTransactionClick(transaction, e)}
                  className={`rounded-md p-3 border hover:shadow-md transition-shadow cursor-pointer ${
                    isSaving
                      ? "bg-blue-50 border-blue-200"
                      : isInEditMode
                      ? "bg-purple-50 border-purple-200"
                      : !transaction.checked
                      ? "bg-red-50 border-red-200"
                      : "bg-gray-50 border-gray-200"
                  } ${isDeleted ? "opacity-40" : ""}`}
                >
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-1 text-sm font-medium text-gray-900">
                    {isInEditMode ? (
                      <input
                        type="date"
                        value={editValues!.date}
                        onChange={(e) => handleEditValueChange('date', e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    ) : (
                      formatDate(transaction.date)
                    )}
                  </div>
                  <div className="col-span-1 text-sm text-gray-700">
                    {isInEditMode && transactionValuesData?.groups ? (
                      <select
                        value={editValues!.groupId}
                        onChange={(e) => handleEditValueChange('groupId', e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {transactionValuesData.groups.map((group) => (
                          <option key={group.id} value={group.id}>
                            {group.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <>
                    <div>{transaction.group.name}</div>
                    {transaction.user && (
                      <div className="text-gray-600">{transaction.user.firstname}</div>
                        )}
                      </>
                    )}
                    {isInEditMode && transactionValuesData?.groups && (
                      <div className="mt-2">
                        <select
                          value={editValues!.userId || ''}
                          onChange={(e) => handleEditValueChange('userId', e.target.value || null)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Aucun utilisateur</option>
                          {getUsersForGroup(editValues!.groupId).map((user) => (
                            <option key={user.id} value={user.id}>
                              {user.firstname}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                  <div className="col-span-2 text-sm text-gray-700">
                    {isInEditMode && transactionValuesData?.groups ? (
                      <select
                        value={editValues!.accountId}
                        onChange={(e) => handleEditValueChange('accountId', e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {getAvailableAccounts(transactionValuesData, editValues!.groupId, editValues!.userId, transaction.account.id).map((account) => (
                          <option key={account.id} value={account.id}>
                            {formatAccountLabel(account.name, account.enabled)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      transaction.account.name
                    )}
                  </div>
                  <div className="col-span-2 text-sm text-gray-700">
                    {isInEditMode && transactionValuesData?.groups ? (
                      <>
                        <select
                          value={editValues!.contextId}
                          onChange={(e) => handleEditValueChange('contextId', e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-2"
                        >
                          <option value="">Sélectionner un contexte</option>
                          {getContextsForGroup(editValues!.groupId).map((context) => (
                            <option key={context.id} value={context.id}>
                              {context.name}
                            </option>
                          ))}
                        </select>
                        {editValues!.contextId && (
                          <select
                            value={editValues!.postId}
                            onChange={(e) => handleEditValueChange('postId', e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="">Sélectionner un post</option>
                            {getPostsForContext(editValues!.groupId, editValues!.contextId).map((post) => (
                              <option key={post.id} value={post.id}>
                                {post.name}
                              </option>
                            ))}
                          </select>
                        )}
                      </>
                    ) : (
                      <>
                    <div>{transaction.context.name}</div>
                    <div className="text-gray-600">{transaction.post.name}</div>
                      </>
                    )}
                  </div>
                  <div className="col-span-3 text-sm text-gray-600 italic">
                    {isInEditMode ? (
                      <textarea
                        value={editValues!.comment}
                        onChange={(e) => handleEditValueChange('comment', e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        rows={2}
                        placeholder="-"
                      />
                    ) : (
                      transaction.comment || "-"
                    )}
                  </div>
                <div className="col-span-2 text-right text-sm font-semibold text-gray-900">
                  {isInEditMode ? (
                    <input
                      type="number"
                      value={editValues!.amount}
                      onChange={(e) => handleEditValueChange('amount', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      step="0.01"
                      className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Montant (€)"
                    />
                  ) : (
                    formatAmount(transaction.credit - transaction.debit)
                  )}
                </div>
                  <div className="col-span-1 text-sm text-gray-500 flex items-center gap-1">
                    {isSaving ? (
                      // Pendant la sauvegarde, aucune action n'est affichée
                      null
                    ) : isInEditMode ? (
                      <>
                        {transaction.checked && (
                          <button
                            onClick={(e) => handleUncheckTransaction(transaction, e)}
                            disabled={editing}
                            className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Décocher la transaction"
                          >
                            <AiOutlineCloseCircle className="text-lg" />
                          </button>
                        )}
                        <button
                          onClick={(e) => handleSaveEdit(transaction, e)}
                          disabled={editing}
                          className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Enregistrer les modifications"
                        >
                          <AiOutlineSave className="text-lg" />
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="p-1 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                          title="Annuler l'édition"
                        >
                          <AiOutlineClose className="text-lg" />
                        </button>
                      </>
                    ) : isDeleted ? (
                      <button
                        onClick={(e) => handleRestoreTransaction(transaction.id, e)}
                        disabled={restoring}
                        className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Restaurer la transaction"
                      >
                        <AiOutlineUndo className="text-lg" />
                      </button>
                    ) : (
                      <div className="flex items-center gap-1">
                        {!transaction.checked ? (
                          <button
                            onClick={(e) => handleCheckTransaction(transaction, e)}
                        disabled={editingTransactionIds.has(transaction.id)}
                        className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Marquer comme vérifiée"
                      >
                        <AiOutlineCheck className="text-lg" />
                      </button>
                        ) : (
                          // Espaceur invisible pour aligner les boutons avec les transactions non vérifiées
                          <div className="p-1" aria-hidden="true">
                            <div className="w-6 h-6" />
                          </div>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); setDrawerTransaction(transaction); }}
                          className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
                          title="Voir les détails"
                        >
                          <AiOutlineEye className="text-lg" />
                        </button>
                        <button
                          onClick={(e) => handleDuplicateTransaction(transaction, e)}
                          disabled={duplicating}
                          className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Dupliquer la transaction"
                        >
                          <AiOutlineCopy className="text-lg" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              );
            })}
            {allTransactions.length === 0 && (
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 text-center text-gray-500 italic">
                Aucune transaction disponible pour le moment.
              </div>
            )}
          </div>
        </div>
      </div>
      {drawerTransaction && (
        <TransactionDetailDrawer
          transaction={drawerTransaction}
          onClose={() => setDrawerTransaction(null)}
          onDelete={async () => {
            await handleDeleteTransaction(drawerTransaction.id);
            setDrawerTransaction(null);
          }}
          deleting={deleting}
          onFilter={({ contextIds, postIds, accountIds, groupIds, autoMatchLabel }) => {
            const contextId = contextIds?.[0];
            const postId = postIds?.[0];
            const accountId = accountIds?.[0];
            const groupId = groupIds?.[0];
            if (contextId) {
              setContextFilterInput(contextId);
              setContextFilter(contextId);
            }
            if (postId) {
              setPostFilterInput(postId);
              setPostFilter(postId);
            }
            if (accountId) {
              setAccountFilterInput(accountId);
              setAccountFilter(accountId);
            }
            if (groupId) {
              setGroupFilterInput(groupId);
              setGroupFilter(groupId);
            }
            if (autoMatchLabel) {
              setAutoMatchLabelFilter(autoMatchLabel);
            }
            setShowFilters(true);
            setDrawerTransaction(null);
          }}
        />
      )}
    </>
  );
};

export default Transactions;

